import React, { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Dropdown } from "@mui/base/Dropdown";
import { MenuButton } from "@mui/base/MenuButton";
import { Menu } from "@mui/base/Menu";
import { MenuItem } from "@mui/base/MenuItem";
import {
  BrainCircuit, ChevronDown, Video, HelpCircle,
  BarChart3, ShieldCheck, FileText, UserPlus, Building2,
  DollarSign, Clock, TrendingUp, Scale, Layers,
  Target, Award, Globe, Users2, User, KeyRound,
  X, Package, Monitor, Download, Menu as MenuIcon, LogOut,
  ClipboardCheck,
} from "lucide-react";
import styles from "./admin.module.scss";
import Images from "assets/Images";
import { apiBaseUrl } from "../../../../../services/config";
import { ThemeProvider, ThemeToggle, useTheme } from "context/ThemeContext";

const API = apiBaseUrl;

// ── Plan → modules ─────────────────────────────────────────────────
const PLAN_MODULES = {
  "Basic HRMS":           ["employees","attendance","payroll","performance","compliance","expenses","hirality-meetings"],
  "Advanced HRMS":        ["employees","attendance","payroll","performance","compliance","download-agent","expenses","hirality-meetings"],
  "AI Interview":         ["recruitment","hirality-meetings"],
  "Advanced HRMS + AI":   ["recruitment","employees","attendance","payroll","performance","compliance","download-agent","expenses","hirality-meetings"],
  "HRMS + AI Bundle":     ["recruitment","employees","attendance","payroll","performance","compliance","documents","download-agent","hirality-meetings"],
  "Enterprise":           ["recruitment","employees","attendance","payroll","performance","compliance","documents","download-agent","hirality-meetings"],
  "Starter":              ["employees","attendance","payroll","performance","compliance","documents","hirality-meetings"],
  "Growth":               ["recruitment","employees","attendance","payroll","performance","compliance","documents","download-agent","hirality-meetings"],
  "Professional":         ["recruitment","employees","attendance","payroll","performance","compliance","documents","download-agent","hirality-meetings"],
};

const PLAN_COLOR = {
  "Basic HRMS":         "#00C8FF",
  "Advanced HRMS":      "#0E7490",
  "AI Interview":       "#7C5CFF",
  "Advanced HRMS + AI": "#2563EB",
  "HRMS + AI Bundle":   "#00E5A3",
  "Enterprise":         "#FFB800",
};

// ── Nav ────────────────────────────────────────────────────────────
const NAV = [
  {
    id:"recruitment", label:"Recruitment & Interviews", icon:BrainCircuit,
    paths:["/admin/hrms/jobs","/admin/hrms/applicants","/admin/hrms/offers","/admin/hrms/recruitment","/admin/interviews","/admin/questions","/admin/Users"],
    children:[
      {label:"Dashboard",          to:"/admin/hrms/recruitment", icon:BarChart3 },
      {label:"Applicant Pipeline", to:"/admin/hrms/applicants",  icon:Users2    },
      {label:"Offer Letters",      to:"/admin/hrms/offers",      icon:FileText  },
      // {label:"Interviews",         to:"/admin/interviews",       icon:Video     },
      // {label:"Questions",          to:"/admin/questions",        icon:HelpCircle},
    ],
  },
  {
    id:"employees", label:"Employee Management", icon:Users2,
    paths:["/admin/hrms/employees","/admin/hrms/departments","/admin/hrms/onboarding","/admin/hrms/designations","/admin/users/manage","/admin/users/reporting","/admin/hrms/assets"],
    children:[
      {label:"Employee Directory",  to:"/admin/hrms/employees",    icon:Users2     },
      {label:"Departments",         to:"/admin/hrms/departments",  icon:Building2  },
      {label:"Designations",        to:"/admin/hrms/designations", icon:Award      },
      {label:"Onboarding",          to:"/admin/hrms/onboarding",   icon:UserPlus   },
      {label:"Users & Roles",       to:"/admin/users/manage",      icon:ShieldCheck},
      {label:"Reporting Structure", to:"/admin/users/reporting",   icon:Users2     },
      {label:"Asset Inventory",     to:"/admin/hrms/assets",       icon:Package    },
    ],
  },
  {
    id:"attendance", label:"Attendance & Leave", icon:Clock,
    paths:["/admin/hrms/attendance","/admin/hrms/tracker","/admin/hrms/leaves","/admin/hrms/holidays","/admin/hrms/leave-types","/admin/hrms/leave-policy"],
    children:[
      {label:"Dashboard",      to:"/admin/hrms/attendance",   icon:BarChart3},
      {label:"Daily Tracker",  to:"/admin/hrms/tracker",      icon:Clock    },
      {label:"Leave Requests", to:"/admin/hrms/leaves",       icon:FileText },
      {label:"Leave Policy",   to:"/admin/hrms/leave-policy", icon:Scale    },
      {label:"Holidays",       to:"/admin/hrms/holidays",     icon:Globe    },
      {label:"Comp-Off",       to:"/admin/hrms/comp-off",     icon:Clock    },
    ],
  },
  {
    id:"payroll", label:"Payroll", icon:DollarSign,
    paths:["/admin/hrms/payroll","/admin/hrms/salary","/admin/hrms/payroll/process","/admin/hrms/payslips","/admin/hrms/tax"],
    children:[
      {label:"Payroll Dashboard", to:"/admin/hrms/payroll",         icon:BarChart3 },
      {label:"Salary Structure",  to:"/admin/hrms/salary",          icon:Layers    },
      {label:"Process Payroll",   to:"/admin/hrms/payroll/process", icon:DollarSign},
      {label:"Payslips",          to:"/admin/hrms/payslips",        icon:FileText  },
      {label:"Bonuses",           to:"/admin/hrms/bonuses",         icon:Award     },
      {label:"Tax & Deductions",  to:"/admin/hrms/tax",             icon:Scale     },
      {label:"Tax Configuration", to:"/admin/hrms/tax-config",      icon:Globe     },
      {label:"Reports",           to:"/admin/hrms/tds-reports",     icon:FileText  },
    ],
  },
  {
    id:"performance", label:"Performance", icon:TrendingUp,
    paths:["/admin/hrms/performance"],
    children:[
      {label:"Dashboard",    to:"/admin/hrms/performance",            icon:BarChart3     },
      {label:"Goals & KPIs", to:"/admin/hrms/performance/goals",      icon:Target        },
      {label:"Appraisals",   to:"/admin/hrms/performance/appraisals", icon:Award         },
      {label:"360° Reviews", to:"/admin/hrms/performance/reviews",    icon:ClipboardCheck},
    ],
  },
  {
    id:"compliance", label:"Compliance & Legal", icon:Scale,
    paths:["/admin/hrms/compliance"],
    children:[
      {label:"Documents",       to:"/admin/hrms/compliance/documents", icon:FileText},
      {label:"Logo & Branding", to:"/admin/documents/branding",        icon:Globe   },
    ],
  },
  {
    id:"download-agent", label:"Desktop Agent", icon:Monitor,
    paths:["/admin/agent_dashbaord","/admin/agent_download"],
    children:[
      {label:"Dashboard", to:"/admin/agent_dashbaord", icon:Monitor },
      {label:"Download",  to:"/admin/agent_download",  icon:Download},
    ],
  },
  {
    id:"expenses", label:"Expenses", icon:DollarSign,
    paths:["/admin/hrms/expenses"],
    children:[
      {label:"Overview", to:"/admin/hrms/expenses", icon:BarChart3},
    ],
  },
  {
    id:"hirality-meetings", label:"Hirality Meetings", icon:Video,
    paths:["/admin/hirality-meetings"],
    children:[
      {label:"Meetings", to:"/admin/hirality-meetings", icon:Video},
    ],
  },
];

// ── Logo — inverts to black in light mode ──────────────────────────
const LogoImg = ({ theme }) => {
  const src = Images?.LOGO || Images?.logo || Images?.hirality || Images?.brandLogo;
  // In light mode, invert the logo so white logo becomes dark/black
  const imgStyle = {
    height: 150,
    objectFit: "contain",
    filter: theme === "light" ? "brightness(0) saturate(100%)" : "none",
    transition: "filter 0.25s",
  };
  return src ? (
    <>
      <img src={src} alt="Hirality" style={imgStyle}
        onError={e => {
          e.currentTarget.style.display = "none";
          e.currentTarget.nextSibling.style.display = "flex";
        }}/>
      <div style={{ display:"none", alignItems:"center", justifyContent:"center",
        fontSize:22, fontWeight:900,
        color: theme === "light" ? "#1A2332" : "#00C8FF" }}>
        Hirality
      </div>
    </>
  ) : (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:22, fontWeight:900,
      color: theme === "light" ? "#1A2332" : "#00C8FF" }}>
      Hirality
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// INNER LAYOUT — uses useTheme() for full theme-aware styling
// ══════════════════════════════════════════════════════════════════
function AdminLayoutInner() {
  const { T, theme } = useTheme();
  const navigate    = useNavigate();
  const location    = useLocation();
  const sidebarRef  = useRef(null);

  const [activeMenu,  setActiveMenu]  = useState("recruitment");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 960);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const onResize = () => {
      setScreenWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth > 960);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target))
        setSidebarOpen(false);
    };
    if (sidebarOpen && screenWidth < 961)
      document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [sidebarOpen, screenWidth]);

  const user = (() => {
    try {
      const su = sessionStorage.getItem("platform_user");
      if (su) return JSON.parse(su);
      return JSON.parse(localStorage.getItem("platform_user") || localStorage.getItem("user") || "{}");
    } catch { return {}; }
  })();

  const companyPlan = user.plan || sessionStorage.getItem("company_plan") || "HRMS + AI Bundle";
  const allowedIds  = PLAN_MODULES[companyPlan] || PLAN_MODULES["Basic HRMS"];
  const visibleNAV  = NAV.filter(item => allowedIds.includes(item.id));
  const planColor   = PLAN_COLOR[companyPlan] || "#00C8FF";

  useEffect(() => {
    if (activeMenu === "download-agent") return;
    for (const item of visibleNAV) {
      if (item.paths.some(p => location.pathname.startsWith(p))) {
        setActiveMenu(item.id); break;
      }
    }
  }, [location.pathname]);

  const logout = () => {
    ["platform_token","platform_user","isLoggedIn","company_plan"].forEach(k => {
      sessionStorage.removeItem(k); localStorage.removeItem(k);
    });
    localStorage.removeItem("user");
    navigate("/login");
  };

  const displayName = user.full_name || `${user.first_name||""} ${user.last_name||""}`.trim() || user.username || "Admin";
  const initials    = displayName.split(" ").filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2) || "AD";
  const avatarColor = user.avatar_color || "#00C8FF";
  const roleLbl     = (user.role || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const [showProfile,  setShowProfile]  = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [pwdForm,      setPwdForm]      = useState({ current:"", next:"", confirm:"" });
  const [pwdMsg,       setPwdMsg]       = useState("");

  return (
    <div className={styles.layoutWrapper}
      style={{ background: T.bg, transition: "background 0.25s" }}>

      {/* Mobile overlay */}
      {sidebarOpen && screenWidth < 961 && (
        <div className={`${styles.sidebarOverlay} ${styles.show}`}
          onClick={() => setSidebarOpen(false)}/>
      )}

      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside
        ref={sidebarRef}
        className={`${styles.sidebar} ${sidebarOpen ? styles.open : styles.closed}`}
        style={{
          background:  T.sidebar_bg,
          borderRight: `1px solid ${T.sidebar_border}`,
          transition:  "background 0.25s, border-color 0.25s",
        }}>

        {/* Logo only — plan badge moved to header */}
        <div className={styles.logoContainer}
          style={{ paddingBottom: 8 }}>
          <div style={{ borderRadius:8, overflow:"hidden", flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
            <LogoImg theme={theme}/>
          </div>
        </div>

        {/* Nav */}
        <nav className={styles.navMenu}>
          <ul>
            {visibleNAV.map((item, idx) => {
              const Icon     = item.icon;
              const isActive = activeMenu === item.id;
              const prevItem = visibleNAV[idx - 1];
              const showSec  = item.section && (!prevItem || prevItem.section !== item.section);

              return (
                <React.Fragment key={item.id}>
                  {showSec && (
                    <li className={styles.sectionLabel}
                      style={{ color: T.dim }}>{item.section}</li>
                  )}
                  <li className={isActive ? styles.active : ""}>
                    <a
                      onClick={() => setActiveMenu(isActive ? "" : item.id)}
                      style={{
                        display:     "flex",
                        alignItems:  "center",
                        gap:         10,
                        padding:     "10px 14px",
                        cursor:      "pointer",
                        borderRadius: 8,
                        color:       isActive ? T.nav_text_active : T.nav_text,
                        background:  isActive ? T.sidebar_active  : "transparent",
                        transition:  "background 0.2s, color 0.2s",
                      }}
                      onMouseEnter={e => {
                        if (!isActive) e.currentTarget.style.background = T.sidebar_hover;
                      }}
                      onMouseLeave={e => {
                        if (!isActive) e.currentTarget.style.background = "transparent";
                      }}>
                      <span className={styles.menuIcon}>
                        <Icon size={16} style={{ color: isActive ? T.nav_text_active : T.nav_text }}/>
                      </span>
                      <p style={{ margin:0, flex:1, fontSize:13,
                        color:      isActive ? T.nav_text_active : T.nav_text,
                        fontWeight: isActive ? 700 : 500 }}>
                        {item.label}
                      </p>
                      <ChevronDown size={14} style={{
                        color:     T.nav_text,
                        transform: isActive ? "rotate(180deg)" : "none",
                        transition: "transform 0.2s",
                      }}/>
                    </a>
                    {isActive && (
                      <ul className={styles.submenu}
                        style={{ background: T.submenu_bg }}>
                        {item.children.map(child => (
                          <li key={child.to}>
                            <NavLink
                              to={child.to}
                              onClick={() => { if (screenWidth < 961) setSidebarOpen(false); }}
                              style={({ isActive: ca }) => ({
                                display:    "flex",
                                alignItems: "center",
                                gap:        8,
                                padding:    "8px 14px 8px 36px",
                                color:      ca ? T.nav_text_active : T.nav_text,
                                fontWeight: ca ? 700 : 400,
                                background: ca ? T.sidebar_active  : "transparent",
                                borderRadius: 6,
                                fontSize:   13,
                                textDecoration: "none",
                                transition: "all 0.15s",
                              })}>
                              {child.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* ── Main ────────────────────────────────────────────────── */}
      <main
        className={`${styles.outletWrapper} ${!sidebarOpen ? styles.sidebarOp : ""}`}
        style={{ background: T.bg, transition: "background 0.25s" }}>

        {/* ── Header ─────────────────────────────────────────── */}
        <div
          className={`${styles.header} ${sidebarOpen ? styles.flexEnd : styles.spaceBetween}`}
          style={{
            background:   T.header_bg,
            borderBottom: `1px solid ${T.header_border}`,
            transition:   "background 0.25s, border-color 0.25s",
          }}>

          {/* Hamburger */}
          <div
            className={`${styles.menuContainer} ${sidebarOpen ? styles.closeIcon : styles.openIcon}`}
            onClick={() => setSidebarOpen(p => !p)}
            style={{ color: T.muted, cursor: "pointer" }}>
            <MenuIcon size={18}/>
          </div>

          {/* Right side: plan badge + theme toggle + user */}
          <div className={styles.headerActions}
            style={{ display:"flex", alignItems:"center", gap:10 }}>

            {/* ── Plan badge — moved from sidebar to header ── */}
            <div style={{
              display:    "inline-flex",
              alignItems: "center",
              gap:        5,
              padding:    "4px 12px",
              borderRadius: 20,
              background: `${planColor}15`,
              border:     `1px solid ${planColor}35`,
            }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:planColor }}/>
              <span style={{
                fontSize:      10,
                fontWeight:    700,
                color:         planColor,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                whiteSpace:    "nowrap",
              }}>
                {companyPlan}
              </span>
            </div>

            {/* ── Theme toggle ── */}
            <ThemeToggle/>

            {/* ── User dropdown ── */}
            <Dropdown>
              <MenuButton style={{ background:"none", border:"none", padding:0, cursor:"pointer" }}>
                <div className={styles.userBtn}>
                  <div className={styles.avatar}
                    style={{ background:avatarColor, color:"#fff",
                      display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>
                    {initials}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start" }}>
                    <span style={{ fontSize:13, fontWeight:600, color:T.text, lineHeight:1.2 }}>
                      {displayName}
                    </span>
                    <span style={{ fontSize:10.5, color:T.muted, lineHeight:1.2 }}>
                      {roleLbl}
                    </span>
                  </div>
                  <ChevronDown size={13} style={{ color:T.muted }}/>
                </div>
              </MenuButton>

              <Menu style={{
                background:  T.dropdown_bg,
                border:     `1px solid ${T.border}`,
                borderRadius: 10,
                padding:     "6px",
                minWidth:    200,
                boxShadow:   T.shadow,
                zIndex:      9999,
                fontFamily:  "Outfit,sans-serif",
              }}>
                <div style={{ padding:"10px 14px", borderBottom:`1px solid ${T.border}`, marginBottom:4 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{displayName}</div>
                  <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{user.email}</div>
                  <div style={{ marginTop:5, display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                    <span style={{ fontSize:10.5, padding:"2px 8px", borderRadius:10,
                      background:`${T.cyan}18`, color:T.cyan, fontWeight:600 }}>{roleLbl}</span>
                    {companyPlan && (
                      <span style={{ fontSize:10.5, padding:"2px 8px", borderRadius:10,
                        background:`${planColor}18`, color:planColor, fontWeight:600 }}>{companyPlan}</span>
                    )}
                  </div>
                </div>

                {[
                  { label:"My Profile",     icon:<User size={14} style={{color:T.cyan}}/>,    onClick:()=>setShowProfile(true)  },
                  { label:"Reset Password", icon:<KeyRound size={14} style={{color:T.amber}}/>, onClick:()=>setShowResetPwd(true) },
                ].map(item => (
                  <MenuItem key={item.label} onClick={item.onClick}
                    style={{ display:"flex", alignItems:"center", gap:9, padding:"9px 14px",
                      borderRadius:7, cursor:"pointer", color:T.text, fontSize:13,
                      fontFamily:"Outfit,sans-serif" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.sidebar_hover}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {item.icon} {item.label}
                  </MenuItem>
                ))}

                <div style={{ height:1, background:T.border, margin:"4px 0" }}/>

                <MenuItem onClick={logout}
                  style={{ display:"flex", alignItems:"center", gap:9, padding:"9px 14px",
                    borderRadius:7, cursor:"pointer", color:T.red, fontSize:13,
                    fontFamily:"Outfit,sans-serif" }}
                  onMouseEnter={e => e.currentTarget.style.background = `${T.red}10`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <LogOut size={14}/> Sign Out
                </MenuItem>
              </Menu>
            </Dropdown>
          </div>
        </div>

        {/* ── Page content — consistent padding for ALL pages ── */}
        <div className={styles.wrapper}
          style={{
            padding:   "28px 32px",
            minHeight: "calc(100vh - 64px)",
            boxSizing: "border-box",
          }}>
          <Outlet/>
        </div>
      </main>

      {/* ── My Profile Modal ──────────────────────────────────── */}
      {showProfile && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"Outfit,sans-serif" }}
          onClick={() => setShowProfile(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.border}`,
            borderRadius:14, width:460, overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`,
              background:T.card2, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>My Profile</h3>
              <button onClick={() => setShowProfile(false)}
                style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}>
                <X size={16}/>
              </button>
            </div>
            <div style={{ padding:24 }}>
              <div style={{ display:"flex", gap:16, alignItems:"center", marginBottom:24 }}>
                <div style={{ width:56, height:56, borderRadius:"50%", background:avatarColor,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:20, fontWeight:800, color:"#fff" }}>{initials}</div>
                <div>
                  <div style={{ fontSize:18, fontWeight:700, color:T.text }}>{displayName}</div>
                  <div style={{ fontSize:12.5, color:T.muted, marginTop:2 }}>{user.email}</div>
                  <div style={{ marginTop:6, display:"flex", gap:6, flexWrap:"wrap" }}>
                    <span style={{ fontSize:11, padding:"2px 10px", borderRadius:10,
                      background:`${T.cyan}18`, color:T.cyan, fontWeight:600 }}>{roleLbl}</span>
                    {companyPlan && (
                      <span style={{ fontSize:11, padding:"2px 10px", borderRadius:10,
                        background:`${planColor}18`, color:planColor, fontWeight:600 }}>{companyPlan}</span>
                    )}
                  </div>
                </div>
              </div>
              {[
                { label:"Email",   val:user.email        },
                { label:"Role",    val:roleLbl           },
                { label:"Plan",    val:companyPlan       },
                { label:"Company", val:user.company_name },
              ].map(({ label, val }) => (
                <div key={label} style={{ display:"flex", justifyContent:"space-between",
                  padding:"10px 0", borderBottom:`1px solid ${T.border}`, fontSize:13 }}>
                  <span style={{ color:T.muted }}>{label}</span>
                  <span style={{ color:T.text, fontWeight:500 }}>{val || "—"}</span>
                </div>
              ))}
              <button onClick={() => { setShowProfile(false); setShowResetPwd(true); }}
                style={{ marginTop:20, width:"100%",
                  background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  border:"none", borderRadius:9, color:"#fff",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600,
                  padding:"11px", cursor:"pointer" }}>
                <KeyRound size={13} style={{ display:"inline", marginRight:6, verticalAlign:"middle" }}/>
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reset Password Modal ──────────────────────────────── */}
      {showResetPwd && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"Outfit,sans-serif" }}
          onClick={() => { setShowResetPwd(false); setPwdMsg(""); }}>
          <div style={{ background:T.card, border:`1px solid ${T.border}`,
            borderRadius:14, width:420, overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`,
              background:T.card2, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Reset Password</h3>
              <button onClick={() => { setShowResetPwd(false); setPwdMsg(""); }}
                style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}>
                <X size={16}/>
              </button>
            </div>
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
              {[
                { key:"current", label:"Current Password",    placeholder:"Your current password" },
                { key:"next",    label:"New Password",         placeholder:"Min 8 characters"     },
                { key:"confirm", label:"Confirm New Password", placeholder:"Repeat new password"  },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5,
                    textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</label>
                  <input type="password" value={pwdForm[key]} placeholder={placeholder}
                    onChange={e => setPwdForm(p => ({ ...p, [key]: e.target.value }))}
                    style={{ width:"100%", background:T.inp_bg,
                      border:`1px solid ${T.border}`, borderRadius:8, color:T.text,
                      fontSize:13, padding:"10px 12px", outline:"none",
                      fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
                </div>
              ))}
              {pwdMsg && (
                <div style={{ padding:"9px 14px", borderRadius:8, fontSize:13,
                  background: pwdMsg.startsWith("✅") ? `${T.green}12` : `${T.red}12`,
                  color:      pwdMsg.startsWith("✅") ? T.green : T.red,
                  border:    `1px solid ${pwdMsg.startsWith("✅") ? T.green : T.red}30` }}>
                  {pwdMsg}
                </div>
              )}
              <button
                onClick={async () => {
                  if (!pwdForm.current || !pwdForm.next) { setPwdMsg("❌ Fill all fields"); return; }
                  if (pwdForm.next !== pwdForm.confirm)  { setPwdMsg("❌ Passwords don't match"); return; }
                  if (pwdForm.next.length < 8)           { setPwdMsg("❌ Min 8 characters"); return; }
                  try {
                    const token = sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token");
                    await (await import("axios")).default.post(
                      `${API}/auth/change-password`,
                      { current_password: pwdForm.current, new_password: pwdForm.next },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setPwdMsg("✅ Password changed successfully");
                    setPwdForm({ current:"", next:"", confirm:"" });
                    setTimeout(() => { setShowResetPwd(false); setPwdMsg(""); }, 2000);
                  } catch(e) {
                    setPwdMsg("❌ " + (e.response?.data?.detail || "Error changing password"));
                  }
                }}
                style={{ background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  border:"none", borderRadius:9, color:"#fff",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700,
                  padding:"11px", cursor:"pointer" }}>
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Outer shell — provides ThemeContext ────────────────────────────
const AdminLayout = () => (
  <ThemeProvider>
    <AdminLayoutInner/>
  </ThemeProvider>
);

export default AdminLayout;